import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useApi } from './hooks/useApi';
import { Header } from './components/Header';
import { TabNav } from './components/TabNav';
import { HomePage } from './pages/HomePage';
import { UpcomingPage } from './pages/UpcomingPage';
import { HistorialPage } from './pages/HistorialPage';
import { AnalysisPage } from './pages/AnalysisPage';
import BetsPage from './pages/BetsPage';
import { SistemaPage } from './pages/SistemaPage';
import { MundialPage } from './pages/MundialPage';
import 'flag-icons/css/flag-icons.min.css';
import './Dashboard.css';

const API = process.env.REACT_APP_API_URL || '/api';

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [selectedLeague, setSelectedLeague] = useState(null);
  const [historialSeason, setHistorialSeason] = useState('all');
  const [analysisSeason, setAnalysisSeason] = useState('all');

  const LEAGUES = [
    { id: null,         name: t('leagues.all'),       isTennis: false },
    { id: 'Tennis',     name: t('leagues.Tennis'),    isTennis: true  },
    { id: 'Baseball',   name: t('leagues.Baseball'),  isBaseball: true },
    { id: 'EPL',        name: t('leagues.EPL'),       isTennis: false },
    { id: 'La_liga',    name: t('leagues.La_liga'),   isTennis: false },
    { id: 'Serie_A',    name: t('leagues.Serie_A'),   isTennis: false },
    { id: 'Bundesliga', name: t('leagues.Bundesliga'),isTennis: false },
    { id: 'Ligue_1',    name: t('leagues.Ligue_1'),   isTennis: false },
  ];

  const isTennis   = selectedLeague === 'Tennis';
  const isBaseball = selectedLeague === 'Baseball';

  // Tennis data
  const [tennisPicks, setTennisPicks]         = useState([]);
  const [tennisIsFallback, setTennisIsFallback] = useState(false);
  const [tennisStats, setTennisStats]         = useState(null);
  const [tennisLoading, setTennisLoading]     = useState(false);
  const [tennisError, setTennisError]         = useState(null);

  const fetchTennisPicks = useCallback(async () => {
    setTennisLoading(true);
    setTennisError(null);
    try {
      const res = await axios.get(`${API}/tennis/picks?min_confidence=0.60&days=30`);
      setTennisPicks(res.data.picks || []);
      setTennisIsFallback(res.data.is_fallback || false);
    } catch (e) {
      setTennisError(e.response?.status || 'error');
    } finally {
      setTennisLoading(false);
    }
  }, []);

  const fetchTennisStats = useCallback(async (season) => {
    try {
      const params = season && season !== 'all' ? `?year=${season}` : '';
      const res = await axios.get(`${API}/tennis/stats${params}`);
      setTennisStats(res.data);
    } catch (e) {}
  }, []);

  const [baseballStats, setBaseballStats]   = useState(null);
  const [baseballPicks, setBaseballPicks]   = useState([]);
  const [baseballLoading, setBaseballLoading] = useState(false);

  const fetchBaseballStats = useCallback(async (season) => {
    try {
      const params = season && season !== 'all' ? `?season=${season}` : '';
      const res = await axios.get(`${API}/baseball/stats${params}`);
      setBaseballStats(res.data);
    } catch (e) {}
  }, []);

  const fetchBaseballPicks = useCallback(async () => {
    setBaseballLoading(true);
    try {
      const res = await axios.get(`${API}/baseball/upcoming?limit=50`);
      const sorted = (res.data || [])
        .filter(g => g.confidence != null)
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 20);
      setBaseballPicks(sorted);
    } catch (e) {}
    setBaseballLoading(false);
  }, []);

  useEffect(() => {
    fetchTennisPicks();
    fetchTennisStats();
    fetchBaseballStats();
    fetchBaseballPicks();
  }, []);

  useEffect(() => {
    const leagueParam = searchParams.get('league');
    const seasonParam = searchParams.get('season');
    if (location.pathname.startsWith('/historial')) {
      if (leagueParam) setSelectedLeague(leagueParam);
      if (seasonParam) setHistorialSeason(seasonParam);
    }
    if (location.pathname.startsWith('/analisis')) {
      if (leagueParam) setSelectedLeague(leagueParam);
      if (seasonParam) {
        setAnalysisSeason(seasonParam);
        fetchPatterns(seasonParam);
      }
    }
  }, [location.pathname, location.search]);

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return 'home';
    if (path.startsWith('/proximos'))   return 'upcoming';
    if (path.startsWith('/historial'))  return 'historial';
    if (path.startsWith('/analisis'))   return 'analisis';
    if (path.startsWith('/prediccion')) return 'predict';
    if (path.startsWith('/apuestas'))   return 'bets';
    if (path.startsWith('/mundial'))    return 'mundial';
    if (path.startsWith('/sistema'))    return 'sistema';
    return 'home';
  };

  const activeTab = getActiveTab();
  const [betsSeason, setBetsSeason] = useState('all');

  const {
    teams,
    recentMatches,
    upcomingMatches,
    topPicks,
    stats,
    accuracy,
    accuracyByLeague,
    availableSeasons,
    patterns,
    error,
    setError,
    fetchUpcomingMatches,
    fetchAccuracy,
    fetchPatterns,
    fetchTopPicks,
    fetchAccuracyByLeague,
    fetchPredictionDetail,
    fetchTeams,
    fetchMatchContext,
  } = useApi(selectedLeague);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'upcoming')  fetchUpcomingMatches();
      if (activeTab === 'historial') fetchAccuracy();
      if (activeTab === 'analisis')  fetchPatterns(analysisSeason);
    }, 30000);
    return () => clearInterval(interval);
}, [activeTab, analysisSeason, fetchPatterns, fetchUpcomingMatches, fetchAccuracy]);

  useEffect(() => {
    fetchPatterns(analysisSeason);
}, [selectedLeague]);

  const handleTabChange = (tab) => {
    const routes = {
      home:      '/',
      upcoming:  '/proximos',
      historial: '/historial',
      analisis:  '/analisis',
      bets:      '/apuestas',
      predict:   '/prediccion',
      mundial:   '/mundial',
      sistema:   '/sistema',
    };
    navigate(routes[tab] || '/');
  };

  const showLeagueSelector = ['home', 'upcoming', 'historial', 'analisis', 'bets'].includes(activeTab);
  const isMundial = activeTab === 'mundial';

  const [homeSeason, setHomeSeason] = useState('all');

  return (
    <div className="dashboard">
      <Header stats={stats} accuracyByLeague={accuracyByLeague} tennisStats={tennisStats} />
      <TabNav activeTab={activeTab} onTabChange={handleTabChange} />

      {showLeagueSelector && (
        <>
          {/* Desktop: pills */}
          <div className="league-selector league-selector-desktop">
            {LEAGUES.map(lg => (
              <button
                key={lg.id || 'all'}
                className={`league-btn ${selectedLeague === lg.id ? 'active' : ''}`}
                onClick={() => setSelectedLeague(lg.id)}
              >
                {lg.name}
              </button>
            ))}
          </div>
          {/* Mobile: dropdown */}
          <div className="league-selector-mobile">
            <select
              value={selectedLeague ?? ''}
              onChange={e => setSelectedLeague(e.target.value === '' ? null : e.target.value)}
              className="league-select"
            >
              {LEAGUES.map(lg => (
                <option key={lg.id ?? 'all'} value={lg.id ?? ''}>
                  {lg.name}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <div className="content">
        <Routes>
          <Route path="/" element={
          <HomePage
              topPicks={topPicks}
              accuracyByLeague={accuracyByLeague}
              availableSeasons={availableSeasons}
              onRefresh={fetchTopPicks}
              onSeasonChange={(s) => {
                  setHomeSeason(s);
                  fetchAccuracyByLeague(s);
                  fetchAccuracy(s);
                  fetchTennisStats(s);
                  fetchBaseballStats(s);
              }}
              fetchTopPicks={fetchTopPicks}
              fetchMatchContext={fetchMatchContext}
              isTennis={isTennis}
              isBaseball={isBaseball}
              tennisPicks={tennisPicks}
              tennisIsFallback={tennisIsFallback}
              tennisStats={tennisStats}
              tennisLoading={tennisLoading}
              tennisError={tennisError}
              onTennisRefresh={fetchTennisPicks}
              baseballStats={baseballStats}
              baseballPicks={baseballPicks}
              baseballLoading={baseballLoading}
              onBaseballRefresh={fetchBaseballPicks}
          />
      } />
          <Route path="/proximos" element={
            <UpcomingPage
                onRefresh={fetchUpcomingMatches}
                fetchUpcomingMatches={fetchUpcomingMatches}
                selectedLeague={selectedLeague}
                fetchMatchContext={fetchMatchContext}
                isTennis={isTennis}
                isBaseball={isBaseball}
            />
        } />
          <Route path="/historial" element={
            <HistorialPage
              fetchPredictionDetail={fetchPredictionDetail}
              league={selectedLeague}
              accuracy={accuracy}
              onRefreshAccuracy={fetchAccuracy}
              availableSeasons={availableSeasons}
              season={historialSeason}
              onSeasonChange={(s) => {
                setHistorialSeason(s);
                fetchAccuracy(s);
              }}
              isTennis={isTennis}
              isBaseball={isBaseball}
            />
          } />
          <Route path="/analisis" element={
            <AnalysisPage
              patterns={patterns}
              onRefresh={() => fetchPatterns(analysisSeason)}
              league={selectedLeague}
              availableSeasons={availableSeasons}
              season={analysisSeason}
              onSeasonChange={(s) => {
                setAnalysisSeason(s);
                fetchPatterns(s);
              }}
              isTennis={isTennis}
              isBaseball={isBaseball}
            />
          } />
          <Route path="/apuestas" element={
            <BetsPage
              league={selectedLeague}
              season={betsSeason}
              isBaseball={isBaseball}
              isTennis={isTennis}
            />
          } />
          <Route path="/mundial" element={<MundialPage />} />
          <Route path="/sistema" element={<SistemaPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default Dashboard;